import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
  Pagination,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  School as SchoolIcon,
  Groups as GroupsIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  MenuBook as SubjectIcon,
  MeetingRoom as RoomIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse, describeApiError } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { DataTable, FilterBar, SchoolPicker, EmptyState } from './primitives';
import { isTeacher, isSchoolScopedRole, canManageTeachers } from '../../constants/roles';
import { useQueryState } from '../../hooks/useQueryState';

const PAGE_SIZE = 20;

// Doubles as useQueryState()'s schema for the LEFT list only — the selected
// class itself lives in the URL path (:groupId), not a query key, so
// selecting a class is a real navigation (Back returns to the list exactly
// as it was) rather than a filter change.
const QUERY_DEFAULTS = { q: '', school: '', page: 1 };

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// "09:00" -> "9:00 AM", matching StudentDrawer's formatSlotTime — kept as
// its own copy here rather than a shared import so this file has no
// dependency on StudentDrawer.js for one string helper.
function formatSlotTime(hhmm) {
  if (!hhmm) return '';
  const [hStr, mStr] = String(hhmm).split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

// Compact "Mon, Wed · 9:40 AM–10:25 AM" summary for a list row, built from
// the same `schedule` string GET /api/groups already returns (one line per
// slot, "DayName: start-end") rather than a second timeslots fetch per row
// — the list only needs a glance, not the editable detail.
//
// Each line has TWO colons — the day/time separator ("Monday:") and one
// inside each time value itself ("09:40") — so this splits on only the
// FIRST colon (`indexOf`, not a blind `split(':')`, which chops the string
// on every colon and silently drops everything after the time's own ':').
// Days sharing an identical time (the common case — the same class most of
// its own week) are grouped into one line rather than repeating the time
// once per day.
function scheduleSummary(schedule) {
  if (!schedule) return 'No time slots yet';
  const lines = schedule.split('\n').filter((l) => l.trim());
  const byTime = new Map();
  lines.forEach((line) => {
    const sep = line.indexOf(':');
    if (sep === -1) return;
    const dayName = line.slice(0, sep).trim();
    const times = line.slice(sep + 1).trim();
    const dash = times.indexOf('-');
    if (dash === -1) return;
    const start = times.slice(0, dash).trim();
    const end = times.slice(dash + 1).trim();
    const shortDay = DAY_SHORT[DAY_NAMES.indexOf(dayName)] || dayName.slice(0, 3);
    const key = `${start}|${end}`;
    if (!byTime.has(key)) byTime.set(key, { start, end, days: [] });
    byTime.get(key).days.push(shortDay);
  });
  if (byTime.size === 0) return 'No time slots yet';
  return Array.from(byTime.values())
    .map(({ start, end, days }) => `${days.join(', ')} · ${formatSlotTime(start)}–${formatSlotTime(end)}`)
    .join('; ');
}

// ─── Left list row ──────────────────────────────────────────────────────────

function ClassRow({ group, selected, onClick }) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 1.75,
        mb: 1,
        cursor: 'pointer',
        borderRadius: radius.control,
        borderColor: selected ? adminColors.accent : 'rgba(24,24,27,0.08)',
        borderWidth: selected ? 2 : 1,
        backgroundColor: selected ? adminColors.accentLight : adminColors.surfaceRaised,
        transition: 'border-color 0.12s ease, background-color 0.12s ease',
        '&:hover': { borderColor: adminColors.accent },
        '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: adminColors.ink, minWidth: 0 }} noWrap>
          {group.name}
        </Typography>
        <Chip
          label={`${group.member_count ?? 0} student${group.member_count === 1 ? '' : 's'}`}
          size="small"
          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: adminColors.surface, color: adminColors.inkSoft, flexShrink: 0 }}
        />
      </Box>
      <Typography variant="caption" sx={{ color: adminColors.inkSoft, display: 'block', mt: 0.25 }} noWrap>
        {group.school || 'No school'}
        {group.subject ? ` · ${group.subject}` : ''}
      </Typography>
      <Typography variant="caption" sx={{ color: adminColors.inkSoft, display: 'block', mt: 0.25 }} noWrap>
        {scheduleSummary(group.schedule)}
      </Typography>
    </Paper>
  );
}

// ─── New class dialog ───────────────────────────────────────────────────────

function NewClassDialog({ open, onClose, currentUserRole, currentUser, onCreated, notify }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [school, setSchool] = useState('');
  const [subject, setSubject] = useState('');
  const [room, setRoom] = useState('');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);

  const isSchoolScoped = isSchoolScopedRole(currentUserRole);

  useEffect(() => {
    if (open) {
      setName('');
      setSchool(isSchoolScoped ? currentUser?.school || '' : '');
      setSubject('');
      setRoom('');
      setNameError('');
    }
  }, [open, isSchoolScoped, currentUser?.school]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setNameError('Enter a class name.');
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          school: isSchoolScoped ? currentUser?.school : school,
          subject: subject.trim() || undefined,
          room: room.trim() || undefined,
        }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Class created. Add time slots and students to finish setting it up.', 'success');
      onCreated(data.group_id);
    } catch (err) {
      notify(`This class couldn't be created. ${describeAdminError(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New class</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField
            label="Class name"
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
            fullWidth
            required
            autoFocus
            error={Boolean(nameError)}
            helperText={nameError}
            disabled={saving}
          />
          <SchoolPicker
            value={school}
            onChange={setSchool}
            currentUserRole={currentUserRole}
            lockedSchoolName={currentUser?.school}
            allowUnassigned={false}
            required
            label="School"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Subject"
              placeholder="e.g. Biology"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Room"
              placeholder="e.g. 214"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              fullWidth
              disabled={saving}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving || !name.trim()}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
        >
          {saving && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
          Create class
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Add students dialog ────────────────────────────────────────────────────
// Searchable multi-select of same-school students not already on the
// roster. GET /api/users is the one endpoint with the "browse this school's
// students" exception for TEACHER (see fetchAvailableStudents in the legacy
// ClassUserAssignmentView this replaces) — MASTER_ADMIN's results are
// unscoped there, so they're narrowed to the class's own school client-side
// below, same as that view did.

function AddStudentsDialog({ open, onClose, selectedClass, rosterIds, currentUserRole, onAdded, notify }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checked, setChecked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setSearch('');
    setChecked(new Set());
    return undefined;
  }, [open]);

  const fetchCandidates = useCallback(
    (searchValue, signal) => {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams({ role: 'STUDENT', page_size: '50' });
      if (searchValue) qs.set('search', searchValue);
      authFetch(`${config.API_BASE_URL}/api/users?${qs.toString()}`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          setCandidates(data?.users || []);
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setError(describeAdminError(err, "Couldn't load students."));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [navigate]
  );

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => fetchCandidates(search.trim(), controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, search, fetchCandidates]);

  const rosterIdSet = useMemo(() => new Set(rosterIds), [rosterIds]);

  const availableCandidates = useMemo(() => {
    return candidates.filter((c) => {
      if (rosterIdSet.has(c.id)) return false;
      // MASTER_ADMIN's /api/users is unscoped by school — narrow to this
      // class's own school. TEACHER/SCHOOL_ADMIN are already school-scoped
      // server-side, so this is a no-op for them (their own school always
      // equals selectedClass.school).
      if (selectedClass?.school && c.school && c.school !== selectedClass.school) return false;
      return true;
    });
  }, [candidates, rosterIdSet, selectedClass?.school]);

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (checked.size === 0 || !selectedClass) return;
    setSaving(true);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${selectedClass.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.from(checked)),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify(`${checked.size} student${checked.size === 1 ? '' : 's'} added to ${selectedClass.name}.`, 'success');
      onAdded();
      onClose();
    } catch (err) {
      notify(`Couldn't add those students. ${describeAdminError(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Add students to {selectedClass?.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          autoFocus
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid rgba(24,24,27,0.08)', borderRadius: radius.control }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : availableCandidates.length === 0 ? (
            <Typography variant="body2" sx={{ color: adminColors.inkSoft, textAlign: 'center', py: 3, px: 2 }}>
              {search ? 'No matching students.' : `No ${selectedClass?.school || 'eligible'} students left to add.`}
            </Typography>
          ) : (
            availableCandidates.map((c) => (
              <Box
                key={c.id}
                onClick={() => toggle(c.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.25,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: adminColors.surface },
                }}
              >
                <Checkbox checked={checked.has(c.id)} size="small" sx={TOUCH_HIT_SX} onClick={(e) => e.stopPropagation()} onChange={() => toggle(c.id)} />
                <Typography variant="body2" sx={{ color: adminColors.ink }}>{c.username}</Typography>
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={checked.size === 0 || saving}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
        >
          {saving && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
          Add {checked.size > 0 ? checked.size : ''} student{checked.size === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit class details dialog (Subject / Room) ─────────────────────────────
// Phase 4 (admin console v2) — the class detail header's own edit
// affordance for the two fields the mockups added (`Group.subject`/`.room`,
// see app/models.py). Deliberately narrower than a full "rename class"
// dialog: there is still no UI anywhere for editing a class's name/school
// after creation (PUT /groups/{id} supports it, but nothing calls that path
// today), and adding one is out of scope for this phase — this dialog only
// ever sends `{subject, room}`.

function EditClassDetailsDialog({ open, onClose, selectedClass, onSaved, notify }) {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [room, setRoom] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && selectedClass) {
      setSubject(selectedClass.subject || '');
      setRoom(selectedClass.room || '');
    }
  }, [open, selectedClass]);

  const handleSave = async () => {
    if (!selectedClass) return;
    setSaving(true);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${selectedClass.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: subject.trim(), room: room.trim() }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Class details updated.', 'success');
      onSaved();
    } catch (err) {
      notify(`Couldn't update this class's details. ${describeAdminError(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit {selectedClass?.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField
            label="Subject"
            placeholder="e.g. Biology"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            autoFocus
            disabled={saving}
          />
          <TextField
            label="Room"
            placeholder="e.g. 214"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            fullWidth
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
        >
          {saving && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Time slots section ─────────────────────────────────────────────────────

// "1st Period (Day A) · 8:00 AM–8:50 AM" — a period's label plus, when it's
// pinned to one rotation day (`day_pattern` non-null — see Period's
// docstring in app/models.py; null means "every rotation day"), which one.
function periodOptionLabel(p) {
  const dayPart = p.day_pattern ? ` (Day ${p.day_pattern})` : '';
  return `${p.label}${dayPart} · ${formatSlotTime(p.start_time)}–${formatSlotTime(p.end_time)}`;
}

// GET /groups/{id}/conflicts (Phase 4, admin console v2) — debounced,
// best-effort preview of what a PROPOSED slot (not yet saved) would
// collide with. Shared by the "add a time slot" bar and TimeSlotRow's own
// inline edit below, so both surface the exact same warning shape. Never
// blocks anything — see this hook's only consumers, both of which render
// its result as a dismissable-by-editing Alert next to a submit button
// that's never disabled by it.
function useSlotConflicts(navigate, groupId, { dayOfWeek, periodId, start, end, excludeSlotId }, enabled) {
  const [state, setState] = useState({ checking: false, conflicts: [] });

  useEffect(() => {
    const hasTime = periodId != null || (start && end);
    if (!enabled || !groupId || dayOfWeek === undefined || dayOfWeek === null || !hasTime) {
      setState({ checking: false, conflicts: [] });
      return undefined;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, checking: true }));
    // Debounced — every keystroke in a custom start/end time field would
    // otherwise fire its own request.
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ day_of_week: String(dayOfWeek) });
      if (periodId != null) params.set('period_id', String(periodId));
      else {
        params.set('start_time', start);
        params.set('end_time', end);
      }
      if (excludeSlotId != null) params.set('exclude_slot_id', String(excludeSlotId));
      authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/conflicts?${params.toString()}`, {}, { signal: controller.signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || controller.signal.aborted) return; // 401 — already redirected
          setState({ checking: false, conflicts: data.conflicts || [] });
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || controller.signal.aborted) return;
          // Best-effort/advisory only — a failed conflict check shouldn't
          // block or scare anyone; just show nothing rather than an error
          // banner next to a time picker.
          setState({ checking: false, conflicts: [] });
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [navigate, groupId, dayOfWeek, periodId, start, end, excludeSlotId, enabled]);

  return state;
}

// Warns, never blocks — see this file's Phase 4 conflict-detection
// comments above. Renders nothing while the debounce/request is in flight
// past its first tick or once it resolves clean, so a slot with no
// conflicts never shows a flash of empty chrome.
function ConflictWarning({ checking, conflicts }) {
  if (checking) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
        <CircularProgress size={13} />
        <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>
          Checking for schedule conflicts…
        </Typography>
      </Box>
    );
  }
  if (!conflicts || conflicts.length === 0) return null;
  return (
    <Alert
      severity="warning"
      variant="outlined"
      icon={<WarningAmberIcon fontSize="small" />}
      sx={{ mt: 1, width: '100%' }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {conflicts.length === 1 ? 'Possible conflict' : `${conflicts.length} possible conflicts`} — you can still
        save this.
      </Typography>
      {conflicts.map((c) => (
        <Typography key={`${c.type}-${c.group_id}-${c.slot_id}`} variant="body2">
          {c.reason}
        </Typography>
      ))}
    </Alert>
  );
}

function TimeSlotRow({ group, slotGroup, canEdit, onEdit, onRemove, removing, periods }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [day, setDay] = useState(slotGroup.day_of_week);
  const [start, setStart] = useState(slotGroup.start_time);
  const [end, setEnd] = useState(slotGroup.end_time);
  const [periodId, setPeriodId] = useState(slotGroup.period_id || '');
  // Explicit toggle, not purely derived from `periods.length` — a slot
  // created before this school had a template (period_id null) should
  // still default to editing as a custom time even after a template
  // exists, rather than silently forcing it onto a period the admin never
  // chose.
  const [mode, setMode] = useState(slotGroup.period_id ? 'period' : 'custom');
  const [saving, setSaving] = useState(false);
  const isMerged = slotGroup.ids.length > 1;
  const hasPeriods = periods && periods.length > 0;

  const startEdit = () => {
    setDay(slotGroup.day_of_week);
    setStart(slotGroup.start_time);
    setEnd(slotGroup.end_time);
    setPeriodId(slotGroup.period_id || '');
    setMode(slotGroup.period_id ? 'period' : 'custom');
    setEditing(true);
  };

  const conflictState = useSlotConflicts(
    navigate,
    group.id,
    {
      dayOfWeek: day,
      periodId: mode === 'period' && periodId ? periodId : null,
      start: mode === 'custom' ? start : null,
      end: mode === 'custom' ? end : null,
      excludeSlotId: slotGroup.ids[0],
    },
    editing && !isMerged
  );

  const canSave = mode === 'period' ? Boolean(periodId) : Boolean(start && end);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const payload = mode === 'period'
      ? { day_of_week: day, period_id: periodId }
      : { day_of_week: day, start_time: start, end_time: end, period_id: null };
    const ok = await onEdit(slotGroup.ids[0], payload);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={day} onChange={(e) => setDay(Number(e.target.value))} disabled={saving}>
              {DAY_NAMES.map((d, i) => (
                <MenuItem key={d} value={i}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {mode === 'period' ? (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={periodId}
                displayEmpty
                onChange={(e) => setPeriodId(e.target.value)}
                disabled={saving}
              >
                <MenuItem value="">
                  <em>Choose a period…</em>
                </MenuItem>
                {periods.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{periodOptionLabel(p)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <TextField
                type="time"
                size="small"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                disabled={saving}
                sx={{ minWidth: 120 }}
              />
              <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>to</Typography>
              <TextField
                type="time"
                size="small"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                disabled={saving}
                sx={{ minWidth: 120 }}
              />
            </>
          )}
          <IconButton size="small" onClick={save} disabled={saving || !canSave} sx={{ ...TOUCH_HIT_SX, color: adminColors.accent }} aria-label="Save time slot">
            {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft }} aria-label="Cancel edit">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {hasPeriods && (
          <Button
            size="small"
            onClick={() => setMode(mode === 'period' ? 'custom' : 'period')}
            disabled={saving}
            sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.75rem', color: adminColors.accent, ...TOUCH_HIT_SX }}
          >
            {mode === 'period' ? 'Use a custom time instead' : 'Use a period instead'}
          </Button>
        )}
        <ConflictWarning checking={conflictState.checking} conflicts={conflictState.conflicts} />
      </Box>
    );
  }

  const periodLabel = slotGroup.period_id ? periods?.find((p) => p.id === slotGroup.period_id)?.label : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid rgba(24,24,27,0.06)' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink, minWidth: 90 }}>
        {DAY_NAMES[slotGroup.day_of_week] || `Day ${slotGroup.day_of_week}`}
      </Typography>
      {periodLabel && (
        <Chip label={periodLabel} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: adminColors.accentLight, color: adminColors.accentDark }} />
      )}
      <Typography variant="body2" sx={{ color: adminColors.inkSoft, flex: 1, minWidth: 0 }}>
        {formatSlotTime(slotGroup.start_time)}–{formatSlotTime(slotGroup.end_time)}
      </Typography>
      {isMerged && (
        <Chip label={`${slotGroup.ids.length}× duplicate, merged`} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
      )}
      {canEdit && (
        <>
          <Tooltip title={isMerged ? 'Remove the duplicates first to edit this time' : 'Edit'}>
            <span>
              <IconButton size="small" onClick={startEdit} disabled={isMerged} sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft, '&:hover': { color: adminColors.accent } }} aria-label={`Edit ${DAY_NAMES[slotGroup.day_of_week]} time slot`}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove">
            <span>
              <IconButton size="small" onClick={() => onRemove(slotGroup.ids)} disabled={removing} sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft, '&:hover': { color: '#dc2626' } }} aria-label={`Remove ${DAY_NAMES[slotGroup.day_of_week]} time slot`}>
                {removing ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export default function ClassesPage({ groups, onRefreshData, currentUserRole, currentUser, notify, confirmAction }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId } = useParams();
  const [qs, setQueryState] = useQueryState(QUERY_DEFAULTS);

  const isSchoolLocked = isSchoolScopedRole(currentUserRole);
  const isTeacherRole = isTeacher(currentUserRole);
  const canEditTeachers = canManageTeachers(currentUserRole);

  // ── Left list ──────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);

  const fetchClasses = useCallback(
    (signal) => {
      setLoadingList(true);
      setListError(null);
      const params = new URLSearchParams({ page: String(qs.page), page_size: String(PAGE_SIZE) });
      if (qs.q) params.set('q', qs.q);
      if (!isSchoolLocked && qs.school) params.set('school', qs.school);
      authFetch(`${config.API_BASE_URL}/api/groups?${params.toString()}`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          const newTotal = data.total || 0;
          setRows(data.items || []);
          setTotal(newTotal);
          // A deep link (or a filter change) past the real last page would
          // otherwise render an empty list with the Pagination `page` prop
          // out of range — clamp here, inline in the fetch success handler
          // using this response's own total, matching the fix applied to
          // StudentsPage.js/SafetyFlagsView.js for the same class of bug.
          const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
          if (qs.page > newTotalPages) {
            setQueryState({ page: newTotalPages });
          }
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setListError(`Couldn't load classes. ${describeApiError(err)}`);
        })
        .finally(() => {
          if (!signal?.aborted) setLoadingList(false);
        });
    },
    [qs.q, qs.school, qs.page, isSchoolLocked, navigate, setQueryState]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchClasses(controller.signal);
    return () => controller.abort();
  }, [fetchClasses]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ── Selected class ────────────────────────────────────────────────────
  // Sourced from the `groups` prop (AdminDashboardV2's already-fetched,
  // unpaginated, role-scoped full list) rather than the left list's own
  // fetch above — the left list is filtered/paginated for browsing, but a
  // deep link (or a class that fell off the current filtered page) must
  // still resolve to its detail. `groups` always has every class this
  // viewer can see.
  const selectedClass = useMemo(() => {
    if (!groupId) return null;
    return (groups || []).find((g) => String(g.id) === groupId) || null;
  }, [groups, groupId]);

  const selectClass = (id) => navigate({ pathname: `/admin/classes/${id}`, search: location.search });
  const backToList = () => navigate({ pathname: '/admin/classes', search: location.search });

  // ── Schedule template for the selected class's school (Phase 4) ────────
  // Powers the Period-based slot picker below: when the class's school has
  // a SchoolScheduleTemplate, the admin picks a Period instead of typing
  // times (see the task spec). Resolved school-name -> id once via
  // GET /api/admin/schools (the same endpoint SchoolPicker/SchoolManagement
  // already use), then the template itself is fetched and cached per
  // school id — switching between two classes at the SAME school never
  // re-fetches.
  const [schoolIdByName, setSchoolIdByName] = useState({});
  useEffect(() => {
    const controller = new AbortController();
    authFetch(`${config.API_BASE_URL}/api/admin/schools`, {}, { signal: controller.signal })
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        const map = {};
        (data.schools || []).forEach((s) => {
          if (s.id != null && s.name) map[s.name.trim().toLowerCase()] = s.id;
        });
        setSchoolIdByName(map);
      })
      .catch(() => {
        // Non-fatal — the Period picker below just degrades to "no
        // periods available" (identical to a school with no template),
        // same as it does for a real school-without-a-template.
      });
    return () => controller.abort();
  }, [navigate]);

  const selectedSchoolId = selectedClass?.school
    ? schoolIdByName[selectedClass.school.trim().toLowerCase()]
    : undefined;

  const [scheduleTemplatesBySchoolId, setScheduleTemplatesBySchoolId] = useState({});
  const requestedTemplateSchoolIds = useRef(new Set());
  useEffect(() => {
    if (selectedSchoolId == null || requestedTemplateSchoolIds.current.has(selectedSchoolId)) return undefined;
    requestedTemplateSchoolIds.current.add(selectedSchoolId);
    const controller = new AbortController();
    setScheduleTemplatesBySchoolId((prev) => ({ ...prev, [selectedSchoolId]: { loading: true, periods: [] } }));
    authFetch(`${config.API_BASE_URL}/api/admin/schools/${selectedSchoolId}/schedule-template`, {}, { signal: controller.signal })
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        const sortedPeriods = [...(data.periods || [])].sort(
          (a, b) => a.ordinal - b.ordinal || (a.day_pattern || '').localeCompare(b.day_pattern || '')
        );
        setScheduleTemplatesBySchoolId((prev) => ({ ...prev, [selectedSchoolId]: { loading: false, periods: sortedPeriods } }));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // A school with no template (the common case today) resolves this
        // exact same way (200, `periods: []`) — so a real fetch failure
        // degrades to the identical "no periods available" state, never a
        // dead-end error banner blocking the rest of this page.
        setScheduleTemplatesBySchoolId((prev) => ({ ...prev, [selectedSchoolId]: { loading: false, periods: [] } }));
      });
    return () => controller.abort();
  }, [selectedSchoolId, navigate]);

  const periodsForSelectedClass = (selectedSchoolId != null && scheduleTemplatesBySchoolId[selectedSchoolId]?.periods) || [];
  const loadingPeriodsForSelectedClass = selectedSchoolId != null && Boolean(scheduleTemplatesBySchoolId[selectedSchoolId]?.loading);

  // ── Detail: teachers ──────────────────────────────────────────────────
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teachersError, setTeachersError] = useState(null);
  const [addTeacherOpen, setAddTeacherOpen] = useState(false);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [loadingTeacherOptions, setLoadingTeacherOptions] = useState(false);
  const [addingTeacherId, setAddingTeacherId] = useState(null);
  const [removingTeacherId, setRemovingTeacherId] = useState(null);

  const fetchTeachers = useCallback(
    (id, signal) => {
      setLoadingTeachers(true);
      setTeachersError(null);
      authFetch(`${config.API_BASE_URL}/api/groups/${id}/teachers`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          setTeachers(data?.teachers || []);
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setTeachersError(describeAdminError(err, "Couldn't load teachers."));
        })
        .finally(() => {
          if (!signal?.aborted) setLoadingTeachers(false);
        });
    },
    [navigate]
  );

  // ── Detail: time slots ────────────────────────────────────────────────
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [newSlot, setNewSlot] = useState({ day: 0, periodId: '', start: '', end: '' });
  // null = "default to Period when this school has periods, else Custom" —
  // an explicit user pick (via the toggle below) overrides that default
  // until the next class selection. Reset alongside `newSlot` whenever
  // `groupId` changes (see the effect near fetchTeachers/fetchSlots/
  // fetchRoster below).
  const [newSlotModeOverride, setNewSlotModeOverride] = useState(null);
  const [addingSlot, setAddingSlot] = useState(false);
  const [removingSlotKey, setRemovingSlotKey] = useState(null);

  const fetchSlots = useCallback(
    (id, signal) => {
      setLoadingSlots(true);
      setSlotsError(null);
      authFetch(`${config.API_BASE_URL}/api/groups/${id}/timeslots`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          setSlots(data?.time_slots || []);
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setSlotsError(describeAdminError(err, "Couldn't load time slots."));
        })
        .finally(() => {
          if (!signal?.aborted) setLoadingSlots(false);
        });
    },
    [navigate]
  );

  // ── Detail: roster ────────────────────────────────────────────────────
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [rosterError, setRosterError] = useState(null);
  const [rosterSelected, setRosterSelected] = useState([]);
  const [addStudentsOpen, setAddStudentsOpen] = useState(false);
  const [removingStudentId, setRemovingStudentId] = useState(null);

  const fetchRoster = useCallback(
    (id, signal) => {
      setLoadingRoster(true);
      setRosterError(null);
      authFetch(`${config.API_BASE_URL}/api/groups/${id}/members`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          setRoster(data?.members || []);
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setRosterError(describeAdminError(err, "Couldn't load the roster."));
        })
        .finally(() => {
          if (!signal?.aborted) setLoadingRoster(false);
        });
    },
    [navigate]
  );

  useEffect(() => {
    if (!groupId) return undefined;
    const controller = new AbortController();
    fetchTeachers(groupId, controller.signal);
    fetchSlots(groupId, controller.signal);
    fetchRoster(groupId, controller.signal);
    setRosterSelected([]);
    setNewSlot({ day: 0, periodId: '', start: '', end: '' });
    setNewSlotModeOverride(null);
    return () => controller.abort();
  }, [groupId, fetchTeachers, fetchSlots, fetchRoster]);

  useEffect(() => {
    if (!addTeacherOpen || !groupId) return undefined;
    const controller = new AbortController();
    setLoadingTeacherOptions(true);
    const qsParams = new URLSearchParams({ role: 'TEACHER', page_size: '50' });
    if (teacherSearch) qsParams.set('search', teacherSearch);
    const timer = setTimeout(() => {
      authFetch(`${config.API_BASE_URL}/api/users?${qsParams.toString()}`, {}, { signal: controller.signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || controller.signal.aborted) return; // 401 — already redirected
          setTeacherOptions(data?.users || []);
        })
        .catch(() => {})
        .finally(() => {
          if (!controller.signal.aborted) setLoadingTeacherOptions(false);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [addTeacherOpen, teacherSearch, groupId, navigate]);

  const teacherIdSet = useMemo(() => new Set(teachers.map((t) => t.id)), [teachers]);
  const availableTeacherOptions = useMemo(
    () =>
      teacherOptions.filter((t) => {
        if (teacherIdSet.has(t.id)) return false;
        if (selectedClass?.school && t.school && t.school !== selectedClass.school) return false;
        return true;
      }),
    [teacherOptions, teacherIdSet, selectedClass?.school]
  );

  const handleAddTeacher = async (teacherId) => {
    if (!groupId) return;
    setAddingTeacherId(teacherId);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/teachers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([teacherId]),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Teacher added.', 'success');
      fetchTeachers(groupId);
    } catch (err) {
      notify(`Couldn't add that teacher. ${describeAdminError(err)}`, 'error');
    } finally {
      setAddingTeacherId(null);
    }
  };

  const performRemoveTeacher = async (teacher) => {
    setRemovingTeacherId(teacher.id);
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/teachers/${teacher.id}`, { method: 'DELETE' });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      const warningFlag = data?.warning || data?.warning_type || data?.warning_code;
      notify(
        warningFlag
          ? `Teacher removed. ${selectedClass?.name || 'This class'} now has no teacher and won't show up for any teacher until one is added back.`
          : 'Teacher removed.',
        warningFlag ? 'warning' : 'success'
      );
      fetchTeachers(groupId);
    } catch (err) {
      notify(`Couldn't remove that teacher. ${describeAdminError(err)}`, 'error');
    } finally {
      setRemovingTeacherId(null);
    }
  };

  const handleRemoveTeacher = (teacher) => {
    confirmAction({
      title: 'Remove teacher',
      body: `Remove ${teacher.username} from ${selectedClass?.name || 'this class'}?`,
      confirmLabel: 'Remove',
      onConfirm: () => performRemoveTeacher(teacher),
    });
  };

  // Groups identical (day_of_week, start_time, end_time) slots into one
  // visual row per the phase-1 spec ("merge exact duplicate slots
  // client-side and say so") — each group keeps every underlying slot id so
  // Remove can clear all of them in one click; Edit is disabled on a merged
  // group (see TimeSlotRow) since there's no single unambiguous slot to
  // retarget the edit at.
  const slotGroups = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      // period_id folded into the merge key (Phase 4, admin console v2) —
      // two slots at the same day/time but derived from different periods
      // are a real (if unusual) distinction, not a duplicate to merge away.
      const key = `${s.day_of_week}|${s.start_time}|${s.end_time}|${s.period_id ?? ''}`;
      if (!map.has(key)) {
        map.set(key, {
          day_of_week: s.day_of_week,
          start_time: s.start_time,
          end_time: s.end_time,
          period_id: s.period_id ?? null,
          ids: [],
        });
      }
      map.get(key).ids.push(s.id);
    });
    return Array.from(map.values()).sort((a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time));
  }, [slots]);
  const duplicateCount = slots.length - slotGroups.length;

  // Period-vs-custom-time picker (Phase 4, admin console v2) — see
  // periodOptionLabel/useSlotConflicts above. Defaults to Period whenever
  // the class's school has any (the task spec's "the admin picks a period
  // instead of typing times"), but an explicit toggle click always wins —
  // handles both a legitimate off-schedule addition and the fetch not
  // having resolved yet.
  const hasPeriodsForNewSlot = periodsForSelectedClass.length > 0;
  const newSlotMode = newSlotModeOverride ?? (hasPeriodsForNewSlot ? 'period' : 'custom');
  const newSlotCanSave = newSlotMode === 'period' ? Boolean(newSlot.periodId) : Boolean(newSlot.start && newSlot.end);

  const newSlotConflictState = useSlotConflicts(
    navigate,
    groupId,
    {
      dayOfWeek: newSlot.day,
      periodId: newSlotMode === 'period' && newSlot.periodId ? newSlot.periodId : null,
      start: newSlotMode === 'custom' ? newSlot.start : null,
      end: newSlotMode === 'custom' ? newSlot.end : null,
      excludeSlotId: null,
    },
    Boolean(groupId)
  );

  const handleAddSlot = async () => {
    if (!newSlotCanSave || !groupId) return;
    setAddingSlot(true);
    try {
      const payload = newSlotMode === 'period'
        ? { day_of_week: newSlot.day, period_id: newSlot.periodId }
        : { day_of_week: newSlot.day, start_time: newSlot.start, end_time: newSlot.end };
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/timeslots/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Time slot added.', 'success');
      setNewSlot({ day: 0, periodId: '', start: '', end: '' });
      fetchSlots(groupId);
      onRefreshData();
    } catch (err) {
      notify(`Couldn't add that time slot. ${describeAdminError(err)}`, 'error');
    } finally {
      setAddingSlot(false);
    }
  };

  // PUT /groups/{id}/timeslots/{slot_id} — edits this one slot in place.
  // Deliberately NOT the legacy ClassesView pattern of deleting every slot
  // and re-adding them: that rebuild had a real failure window where a
  // half-completed edit could wipe a class's whole schedule (see that
  // view's own comments on existingSlotsFetchFailed/deleteFailures).
  const handleEditSlot = async (slotId, payload) => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/timeslots/${slotId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return false; // 401 — already redirected
      notify('Time slot updated.', 'success');
      fetchSlots(groupId);
      onRefreshData();
      return true;
    } catch (err) {
      notify(`Couldn't update that time slot. ${describeAdminError(err)}`, 'error');
      return false;
    }
  };

  const performRemoveSlots = async (ids) => {
    const key = ids.join(',');
    setRemovingSlotKey(key);
    let failures = 0;
    for (const id of ids) {
      try {
        // eslint-disable-next-line no-await-in-loop -- sequential by design: these are the same logical time slot (duplicates), removed one at a time so a mid-loop failure leaves a knowable remainder rather than firing every DELETE at once
        const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/timeslots/${id}`, { method: 'DELETE' });
        // eslint-disable-next-line no-await-in-loop
        const data = await handleApiResponse(res, navigate);
        if (data === null) return; // 401 — already redirected
      } catch {
        failures += 1;
      }
    }
    setRemovingSlotKey(null);
    fetchSlots(groupId);
    onRefreshData();
    if (failures > 0) {
      notify(`Time slot removed, but ${failures} duplicate${failures === 1 ? '' : 's'} couldn't be — reopen this class to check.`, 'warning');
    } else {
      notify('Time slot removed.', 'success');
    }
  };

  const handleRemoveSlot = (ids) => {
    confirmAction({
      title: 'Remove time slot',
      body: "Remove this time slot? Students won't be able to sign in during this window through this class anymore.",
      confirmLabel: 'Remove',
      onConfirm: () => performRemoveSlots(ids),
    });
  };

  const rosterIds = useMemo(() => roster.map((r) => r.id), [roster]);

  const performRemoveStudent = async (studentIds) => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}/members/bulk-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: studentIds }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      const notFoundCount = (data.not_found || []).length;
      notify(
        notFoundCount
          ? `${data.removed} removed; ${notFoundCount} weren't on the roster.`
          : `${data.removed} student${data.removed === 1 ? '' : 's'} removed from the class.`,
        'success'
      );
      setRosterSelected([]);
      fetchRoster(groupId);
      onRefreshData();
    } catch (err) {
      notify(`Couldn't remove ${studentIds.length === 1 ? 'that student' : 'those students'}. ${describeAdminError(err)}`, 'error');
    } finally {
      setRemovingStudentId(null);
    }
  };

  const handleRemoveOneStudent = (student) => {
    confirmAction({
      title: 'Remove from class',
      body: `Remove ${student.username} from ${selectedClass?.name || 'this class'}?`,
      confirmLabel: 'Remove',
      onConfirm: () => {
        setRemovingStudentId(student.id);
        return performRemoveStudent([student.id]);
      },
    });
  };

  const handleBulkRemoveStudents = () => {
    confirmAction({
      title: 'Remove from class',
      body: `Remove ${rosterSelected.length} student${rosterSelected.length === 1 ? '' : 's'} from ${selectedClass?.name || 'this class'}?`,
      confirmLabel: 'Remove',
      onConfirm: () => performRemoveStudent(rosterSelected),
    });
  };

  const performDeleteClass = async () => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/groups/${groupId}`, { method: 'DELETE' });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      notify('Class deleted.', 'success');
      onRefreshData();
      fetchClasses();
      backToList();
    } catch (err) {
      notify(`This class couldn't be deleted. ${describeAdminError(err)}`, 'error');
    }
  };

  const handleDeleteClass = () => {
    confirmAction({
      title: 'Delete class',
      body: `Delete "${selectedClass?.name}"? This removes every student and teacher from it and cannot be undone.`,
      confirmLabel: 'Delete class',
      onConfirm: performDeleteClass,
    });
  };

  const [newClassOpen, setNewClassOpen] = useState(false);
  const handleClassCreated = (newGroupId) => {
    setNewClassOpen(false);
    onRefreshData();
    fetchClasses();
    if (newGroupId) navigate(`/admin/classes/${newGroupId}`);
  };

  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const handleClassDetailsSaved = () => {
    setEditDetailsOpen(false);
    onRefreshData();
  };

  // Not memoized: `handleRemoveOneStudent` closes over `confirmAction`/
  // `performRemoveStudent`, so it (and therefore this array) would be new
  // on every render regardless — a plain rebuild each render is simpler
  // than a useMemo that can never actually stabilize its own inputs.
  const rosterColumns = [
    {
      key: 'username',
      label: 'Student',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>
          {row.username}
        </Typography>
      ),
    },
    {
      key: 'school',
      label: 'School',
      render: (row) => (
        <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>{row.school || '—'}</Typography>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); handleRemoveOneStudent(row); }}
          disabled={removingStudentId === row.id}
          aria-label={`Remove ${row.username} from this class`}
          sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft, '&:hover': { color: '#dc2626' } }}
        >
          {removingStudentId === row.id ? <CircularProgress size={14} /> : <CloseIcon fontSize="small" />}
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: adminColors.ink }}>Classes</Typography>
          <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.5 }}>
            Schedules and rosters, in one place.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewClassOpen(true)}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark }, borderRadius: radius.control, px: 3, py: 1, ...TOUCH_HIT_SX }}
        >
          New class
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          flex: 1,
          minHeight: 0,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* ── Left: filterable list ── */}
        <Box
          sx={{
            width: { xs: '100%', md: 340 },
            flexShrink: 0,
            display: groupId ? { xs: 'none', md: 'flex' } : 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <FilterBar
            queryState={qs}
            setQueryState={setQueryState}
            defaults={QUERY_DEFAULTS}
            searchKey="q"
            searchPlaceholder="Search classes…"
            resetOnSearch={['page']}
            sx={{ mb: 1.5 }}
          >
            {!isSchoolLocked && (
              <SchoolPicker
                value={qs.school}
                onChange={(v) => setQueryState({ school: v, page: QUERY_DEFAULTS.page })}
                currentUserRole={currentUserRole}
                emptyLabel="All schools"
                label="School"
                size="small"
                fullWidth={false}
                sx={{ minWidth: 140 }}
              />
            )}
          </FilterBar>

          {listError && (
            <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchClasses()}>Retry</Button>}>
              {listError}
            </Alert>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {loadingList ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<GroupsIcon />}
                title={isTeacherRole ? 'No classes yet' : 'No classes found'}
                description={isTeacherRole ? 'Create your first class to get started.' : 'Try a different search or filter.'}
              />
            ) : (
              rows.map((g) => (
                <ClassRow key={g.id} group={g} selected={String(g.id) === groupId} onClick={() => selectClass(g.id)} />
              ))
            )}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
              <Pagination count={totalPages} page={qs.page} onChange={(_, v) => setQueryState({ page: v })} color="primary" shape="rounded" size="small" />
            </Box>
          )}
        </Box>

        {/* ── Right: detail ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: !groupId ? { xs: 'none', md: 'block' } : 'block' }}>
          {!groupId ? (
            <Paper variant="outlined" sx={{ borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)', height: '100%' }}>
              <EmptyState icon={<SchoolIcon />} title="Select a class" description="Pick a class on the left to see its schedule and roster." />
            </Paper>
          ) : !selectedClass ? (
            (groups || []).length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <EmptyState
                icon={<SchoolIcon />}
                title="Class not found"
                description="It may have been deleted, or you may not have access to it."
                action={<Button variant="outlined" onClick={backToList} sx={TOUCH_HIT_SX}>Back to classes</Button>}
              />
            )
          ) : (
            <Box>
              <Button
                onClick={backToList}
                startIcon={<ArrowBackIcon fontSize="small" />}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, mb: 1.5, color: adminColors.inkSoft, fontWeight: 600, ...TOUCH_HIT_SX }}
              >
                Back to classes
              </Button>

              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: adminColors.ink }}>{selectedClass.name}</Typography>
                    <Tooltip title="Edit subject and room">
                      <IconButton
                        size="small"
                        onClick={() => setEditDetailsOpen(true)}
                        aria-label={`Edit ${selectedClass.name}'s subject and room`}
                        sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft, '&:hover': { color: adminColors.accent } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" sx={{ color: adminColors.inkSoft, mt: 0.25 }}>
                    {selectedClass.school || 'No school'} · {selectedClass.member_count ?? 0} student{selectedClass.member_count === 1 ? '' : 's'}
                  </Typography>
                  {(selectedClass.subject || selectedClass.room) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                      {selectedClass.subject && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <SubjectIcon sx={{ fontSize: 16, color: adminColors.inkSoft }} />
                          <Typography variant="body2" sx={{ color: adminColors.ink }}>{selectedClass.subject}</Typography>
                        </Box>
                      )}
                      {selectedClass.room && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RoomIcon sx={{ fontSize: 16, color: adminColors.inkSoft }} />
                          <Typography variant="body2" sx={{ color: adminColors.ink }}>Room {selectedClass.room}</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={handleDeleteClass}
                  sx={TOUCH_HIT_SX}
                >
                  Delete class
                </Button>
              </Box>

              {/* Teachers */}
              <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1.25 }}>Teachers</Typography>
                {teachersError ? (
                  <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchTeachers(groupId)}>Retry</Button>}>{teachersError}</Alert>
                ) : loadingTeachers ? (
                  <CircularProgress size={18} />
                ) : teachers.length === 0 ? (
                  <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: canEditTeachers ? 1.5 : 0 }}>
                    No teacher assigned — this class won't show up for anyone until one is added.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: canEditTeachers ? 1.5 : 0 }}>
                    {teachers.map((t) => (
                      <Chip
                        key={t.id}
                        label={t.username}
                        onDelete={canEditTeachers ? () => handleRemoveTeacher(t) : undefined}
                        deleteIcon={removingTeacherId === t.id ? <CircularProgress size={12} /> : undefined}
                        disabled={removingTeacherId === t.id}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                )}
                {canEditTeachers && (
                  <>
                    <Button
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => setAddTeacherOpen((v) => !v)}
                      sx={{ ...TOUCH_HIT_SX, fontWeight: 600, color: adminColors.accent }}
                    >
                      Add a teacher…
                    </Button>
                    {addTeacherOpen && (
                      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: adminColors.surface, borderRadius: radius.control }}>
                        <TextField
                          size="small"
                          placeholder="Search teachers…"
                          value={teacherSearch}
                          onChange={(e) => setTeacherSearch(e.target.value)}
                          fullWidth
                          sx={{ mb: 1 }}
                        />
                        {loadingTeacherOptions ? (
                          <CircularProgress size={16} />
                        ) : availableTeacherOptions.length === 0 ? (
                          <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>No matching teachers to add.</Typography>
                        ) : (
                          <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                            {availableTeacherOptions.map((t) => (
                              <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                <Typography variant="body2" sx={{ color: adminColors.ink }}>{t.username}</Typography>
                                <Button size="small" onClick={() => handleAddTeacher(t.id)} disabled={addingTeacherId === t.id} sx={TOUCH_HIT_SX}>
                                  {addingTeacherId === t.id ? <CircularProgress size={14} /> : 'Add'}
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Paper>

              {/* Time slots */}
              <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink, mb: 0.75 }}>Time slots</Typography>
                <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 1.5 }}>
                  These are also this class's sign-in window — a student can only sign in while one of these times is active.
                </Typography>
                {duplicateCount > 0 && (
                  <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
                    {duplicateCount} duplicate time slot{duplicateCount === 1 ? '' : 's'} {duplicateCount === 1 ? 'is' : 'are'} merged into the list below.
                  </Alert>
                )}
                {slotsError ? (
                  <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchSlots(groupId)}>Retry</Button>}>{slotsError}</Alert>
                ) : loadingSlots ? (
                  <CircularProgress size={18} />
                ) : slotGroups.length === 0 ? (
                  <Typography variant="body2" sx={{ color: adminColors.inkSoft, mb: 1.5 }}>No time slots yet.</Typography>
                ) : (
                  <Box sx={{ mb: 1.5 }}>
                    {slotGroups.map((sg) => (
                      <TimeSlotRow
                        key={`${sg.day_of_week}-${sg.start_time}-${sg.end_time}-${sg.period_id ?? ''}`}
                        group={selectedClass}
                        slotGroup={sg}
                        canEdit
                        onEdit={handleEditSlot}
                        onRemove={handleRemoveSlot}
                        removing={removingSlotKey === sg.ids.join(',')}
                        periods={periodsForSelectedClass}
                      />
                    ))}
                  </Box>
                )}

                <Divider sx={{ mb: 1.5, borderColor: 'rgba(24,24,27,0.08)' }} />
                {loadingPeriodsForSelectedClass && (
                  <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, mb: 1 }}>
                    Loading {selectedClass.school}&rsquo;s bell schedule…
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="new-slot-day-label">Day</InputLabel>
                    <Select
                      labelId="new-slot-day-label"
                      label="Day"
                      value={newSlot.day}
                      onChange={(e) => setNewSlot((s) => ({ ...s, day: Number(e.target.value) }))}
                    >
                      {DAY_NAMES.map((d, i) => (
                        <MenuItem key={d} value={i}>{d}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {newSlotMode === 'period' ? (
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel id="new-slot-period-label" shrink>Period</InputLabel>
                      <Select
                        labelId="new-slot-period-label"
                        label="Period"
                        displayEmpty
                        value={newSlot.periodId}
                        onChange={(e) => setNewSlot((s) => ({ ...s, periodId: e.target.value }))}
                      >
                        <MenuItem value="">
                          <em>Choose a period…</em>
                        </MenuItem>
                        {periodsForSelectedClass.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{periodOptionLabel(p)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <>
                      <TextField
                        type="time"
                        size="small"
                        label="Start"
                        InputLabelProps={{ shrink: true }}
                        value={newSlot.start}
                        onChange={(e) => setNewSlot((s) => ({ ...s, start: e.target.value }))}
                        sx={{ minWidth: 120 }}
                      />
                      <TextField
                        type="time"
                        size="small"
                        label="End"
                        InputLabelProps={{ shrink: true }}
                        value={newSlot.end}
                        onChange={(e) => setNewSlot((s) => ({ ...s, end: e.target.value }))}
                        sx={{ minWidth: 120 }}
                      />
                    </>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddSlot}
                    disabled={!newSlotCanSave || addingSlot}
                    sx={TOUCH_HIT_SX}
                  >
                    {addingSlot ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                    Add
                  </Button>
                </Box>
                {hasPeriodsForNewSlot && (
                  <Button
                    size="small"
                    onClick={() => setNewSlotModeOverride(newSlotMode === 'period' ? 'custom' : 'period')}
                    sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.75rem', color: adminColors.accent, ...TOUCH_HIT_SX }}
                  >
                    {newSlotMode === 'period' ? 'Use a custom time instead' : 'Use a period instead'}
                  </Button>
                )}
                <ConflictWarning checking={newSlotConflictState.checking} conflicts={newSlotConflictState.conflicts} />
              </Paper>

              {/* Roster */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink }}>
                    Roster ({roster.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonAddIcon fontSize="small" />}
                    onClick={() => setAddStudentsOpen(true)}
                    sx={TOUCH_HIT_SX}
                  >
                    Add students
                  </Button>
                </Box>
                {rosterError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchRoster(groupId)}>Retry</Button>}>{rosterError}</Alert>
                )}
                <DataTable
                  columns={rosterColumns}
                  rows={roster}
                  getRowId={(r) => r.id}
                  selectable
                  selectedIds={rosterSelected}
                  onSelectionChange={setRosterSelected}
                  bulkActions={[{ label: 'Remove from class', icon: <DeleteIcon fontSize="small" />, onClick: handleBulkRemoveStudents, color: 'error' }]}
                  onRowClick={(row) => navigate(`/admin/students/${encodeURIComponent(row.username)}`)}
                  getRowAriaLabel={(row) => `Open ${row.username}`}
                  loading={loadingRoster}
                  emptyState={
                    <EmptyState
                      icon={<PersonAddIcon />}
                      title="No students yet"
                      description="Add students from this school to get the class started."
                      action={<Button variant="contained" onClick={() => setAddStudentsOpen(true)} sx={TOUCH_HIT_SX}>Add students</Button>}
                    />
                  }
                />
              </Paper>
            </Box>
          )}
        </Box>
      </Box>

      <NewClassDialog
        open={newClassOpen}
        onClose={() => setNewClassOpen(false)}
        currentUserRole={currentUserRole}
        currentUser={currentUser}
        onCreated={handleClassCreated}
        notify={notify}
      />

      {selectedClass && (
        <AddStudentsDialog
          open={addStudentsOpen}
          onClose={() => setAddStudentsOpen(false)}
          selectedClass={selectedClass}
          rosterIds={rosterIds}
          currentUserRole={currentUserRole}
          onAdded={() => { fetchRoster(groupId); onRefreshData(); }}
          notify={notify}
        />
      )}

      {selectedClass && (
        <EditClassDetailsDialog
          open={editDetailsOpen}
          onClose={() => setEditDetailsOpen(false)}
          selectedClass={selectedClass}
          onSaved={handleClassDetailsSaved}
          notify={notify}
        />
      )}
    </Box>
  );
}
